import React, { useEffect, useState } from 'react'
import { Button, message } from 'antd'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { API } from 'src/utils/interceptor'
import styled from 'styled-components/macro'

dayjs.extend(utc)

const SupplierOrderTransmission = ({
  supplierId,
  venueId,
  date,
  purchaseOrderNumber,
}) => {
  const [orders, setOrders] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    setOrders(null)
    setFailed(false)
    const query = new URLSearchParams({ page: 1, limit: 200, venueId })
    if (purchaseOrderNumber && purchaseOrderNumber !== 'all') {
      query.set('purchaseOrderNumber', purchaseOrderNumber)
    } else {
      query.set('start', date)
      query.set('end', date)
    }
    API.get('api2', `suppliers/${supplierId}/orders?${query}`)
      .then(
        data =>
          active &&
          setOrders(
            (data.orders || []).filter(order => order.isIntegratedSupplier),
          ),
      )
      .catch(() => {
        if (active) setFailed(true)
      })
    return () => {
      active = false
    }
  }, [supplierId, venueId, date, purchaseOrderNumber])

  if (failed)
    return <Reference>Transmission information could not be loaded.</Reference>
  if (orders === null)
    return <Reference>Loading transmission information…</Reference>
  if (!orders.length) return null

  return (
    <Reference aria-label='Order transmission information'>
      {orders.map(order => (
        <OrderReference key={order._id}>
          <dt>OMS PO:</dt>
          <dd>{order.purchaseOrderNumber || 'Unavailable'}</dd>
          <dt>Account number:</dt>
          <dd>{order.accountNumber || 'Unavailable'}</dd>
          <dt>Transmitted filename:</dt>
          <dd>{order.transmittedFilename || 'Filename unavailable'}</dd>
          {order.transmittedAt && dayjs(order.transmittedAt).isValid() && (
            <>
              <dt>Transmitted:</dt>
              <dd>
                {dayjs(order.transmittedAt)
                  .utc()
                  .format('DD/MM/YYYY HH:mm [UTC]')}
              </dd>
            </>
          )}
        </OrderReference>
      ))}
      <small>Transmission does not confirm supplier receipt or import.</small>
      {orders.some(order => order.transmittedFilename) && (
        <CopyActions>
          {orders.map(
            order =>
              order.transmittedFilename && (
                <CopyToClipboard
                  key={order._id}
                  text={order.transmittedFilename}
                  onCopy={(_, copied) =>
                    copied
                      ? message.success('Filename copied')
                      : message.error('Unable to copy filename')
                  }
                >
                  <CopyFilenameButton type='primary'>
                    Copy filename
                  </CopyFilenameButton>
                </CopyToClipboard>
              ),
          )}
        </CopyActions>
      )}
    </Reference>
  )
}

const Reference = styled.section`
  width: 100%;
  box-sizing: border-box;
  margin: 32px 0 16px;
  padding: 20px 24px;
  background-color: #edf7e7;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  display: grid;
  gap: 12px;
  text-align: left;
`

const CopyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`

const CopyFilenameButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: auto;
    min-height: 48px;
    padding: 12px 22px;
    border: none;
    border-radius: 12px;
    background-color: #bbdc40;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 10px 22px rgba(187, 220, 64, 0.32);
    white-space: nowrap;
  }

  &&:hover,
  &&:focus,
  &&:active {
    background-color: #bbdc40;
    color: #fff;
  }

  &&:hover {
    filter: brightness(1.06);
  }
`

const OrderReference = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 10px 20px;
  margin: 0;
  font-size: 15px;
  line-height: 1.6;

  dt {
    font-weight: 700;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;

    dd:not(:last-child) {
      margin-bottom: 8px;
    }
  }
`

export default SupplierOrderTransmission
